import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Loader } from "@/components/common";
import { Gate } from "@/components/Gate";
import { Login, Register, SetupPassword } from "@/pages/Auth";
import Home from "@/pages/Home";
import Ideas from "@/pages/Ideas";
import IdeaDetail from "@/pages/IdeaReport";
import FindMyBusiness from "@/pages/FindMyBusiness";
import Compare from "@/pages/CompareIdeas";
import Upgrade from "@/pages/Upgrade";
import Plans from "@/pages/Plans";
import ExpertCoaching from "@/pages/ExpertCoaching";
import LaunchPlan from "@/pages/launch/LaunchPlan";
import Promote from "@/pages/launch/Promote";
import Journey from "@/pages/Journey";
import Stage from "@/pages/Stage";
import StageOutput from "@/pages/StageOutput";
import Lesson from "@/pages/Lesson";
import MyBusiness from "@/pages/MyBusiness";
import Checklist from "@/pages/Checklist";
import Help from "@/pages/Help";
import AICoach from "@/pages/AICoach";
import Coaching from "@/pages/Coaching";
import Tools from "@/pages/Tools";
import Resources from "@/pages/Resources";
import Settings from "@/pages/Settings";
import OfficialSources from "@/pages/OfficialSources";
import Admin from "@/pages/admin/Admin";
import Landing from "@/pages/Landing";
import FreeLanding from "@/pages/FreeLanding";
import StartSplit from "@/pages/StartSplit";
import ChallengeLanding from "@/pages/challenge/ChallengeLanding";
import ChallengeDashboard from "@/pages/challenge/ChallengeDashboard";
import ChallengeDay from "@/pages/challenge/ChallengeDay";
import { ChallengeShell } from "@/pages/challenge/ChallengeShell";
import ChallengeAdmin from "@/pages/admin/ChallengeAdmin";
import FreeArea from "@/pages/free/FreeArea";
import AdminUser from "@/pages/admin/AdminUser";
import SalesPage from "@/pages/admin/SalesPage";

function RequireAuth({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={loc.pathname === "/" ? "/welcome" : "/login"} state={{ from: loc.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  if (user.role !== "admin" && !user.permissions?.includes("ideas")) return <FreeArea />;
  return children;
}

function RequireLogin({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  return user ? children : <Navigate to="/challenge" replace />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  return user ? <Navigate to="/" replace /> : children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/welcome" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/free" element={<PublicOnly><FreeLanding /></PublicOnly>} />
      <Route path="/start" element={<PublicOnly><StartSplit /></PublicOnly>} />
      <Route path="/challenge" element={<ChallengeLanding />} />
      <Route path="/dashboard/challenge" element={<RequireLogin><ChallengeDashboard /></RequireLogin>} />
      <Route path="/challenge/day/:day" element={<RequireLogin><ChallengeDay /></RequireLogin>} />
      <Route path="/challenge/builder" element={<RequireLogin><ChallengeShell><Upgrade /></ChallengeShell></RequireLogin>} />
      <Route path="/setup-password" element={<SetupPassword />} />
      <Route path="/lesson/:lessonId" element={<RequireAuth><Gate perm="courses" feature="lessons"><Lesson /></Gate></RequireAuth>} />
      <Route path="/free-idea" element={<RequireAuth><Navigate to="/ideas/20" replace /></RequireAuth>} />
      <Route path="/unlock" element={<RequireAuth><Navigate to="/ideas" replace /></RequireAuth>} />
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route path="/" element={<Home />} />
        <Route path="/ideas" element={<Ideas />} />
        <Route path="/ideas/:number" element={<IdeaDetail />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/find" element={<FindMyBusiness />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/expert-coaching" element={<ExpertCoaching />} />
        <Route path="/launch" element={<LaunchPlan />} />
        <Route path="/launch/promote" element={<Promote />} />
        <Route path="/journey" element={<Gate perm="builder" feature="the Business Journey"><Journey /></Gate>} />
        <Route path="/stage/:key" element={<Gate perm="builder" feature="the Business Journey"><Stage /></Gate>} />
        <Route path="/stage/:key/output" element={<Gate perm="builder" feature="stage outputs"><StageOutput /></Gate>} />
        <Route path="/business" element={<Gate perm="workspace" feature="My Business"><MyBusiness /></Gate>} />
        <Route path="/checklist" element={<Gate perm="checklists" feature="checklists"><Checklist /></Gate>} />
        <Route path="/help" element={<Help />} />
        <Route path="/coach" element={<Gate perm="ai_coach" feature="the AI Business Coach"><AICoach /></Gate>} />
        <Route path="/coaching" element={<Coaching />} />
        <Route path="/tools" element={<Gate perm="tools" feature="recommended tools"><Tools /></Gate>} />
        <Route path="/resources" element={<Gate perm="templates" feature="templates & resources"><Resources /></Gate>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/sources" element={<OfficialSources />} />
        <Route path="/admin" element={<RequireAuth admin><Admin /></RequireAuth>} />
        <Route path="/admin/sales" element={<RequireAuth admin><SalesPage /></RequireAuth>} />
        <Route path="/admin/challenge" element={<RequireAuth admin><ChallengeAdmin /></RequireAuth>} />
        <Route path="/admin/users/:id" element={<RequireAuth admin><AdminUser /></RequireAuth>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
